import { forwardRef } from 'react';
import Box from '@mui/material/Box';
import MuiButton, { type ButtonProps } from '@mui/material/Button';

import { IconArrowRight as ArrowForwardRoundedIcon } from '@/components/icon/tabler/IconArrowRight';
import { surfaceTokens } from '@/theme/themePrimitives';
import { CristaSpinner } from '@/components/Loaders';

interface ButtonLoadingProps extends ButtonProps {
  loading?: boolean;
  /** Adds the compact arrow chip used by the primary Signal CTA. */
  withArrow?: boolean;
  /** Set alongside `href`: MUI renders the root as an anchor, so links need these. */
  target?: string;
  rel?: string;
}

const sx = {
  content: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  startIconSlot: {
    position: 'relative',
    display: 'inline-flex',
  },
  hiddenStartIcon: {
    visibility: 'hidden',
    display: 'inline-flex',
  },
  startIconLoaderContainer: {
    position: 'absolute',
    inset: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrowChip: (variant: ButtonProps['variant'], size: ButtonProps['size']) => {
    const diameter = size === 'xsmall' ? 18 : size === 'small' ? 20 : size === 'large' ? 28 : 24;
    return {
      width: diameter,
      height: diameter,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      borderRadius: '50%',
      backgroundColor: variant === 'contained' ? 'rgba(255,255,255,0.18)' : surfaceTokens.section,
      '& svg': { fontSize: Math.max(12, diameter - 8) },
    };
  },
};

const Button = forwardRef<HTMLButtonElement, ButtonLoadingProps>(
  (
    {
      loading,
      withArrow = false,
      variant = 'contained',
      startIcon,
      children,
      sx: buttonSx,
      ...props
    },
    ref,
  ) => {
    const loaderSize = props.size === 'large' ? 18 : 16;

    const loader = <CristaSpinner size={loaderSize} color="currentColor" />;

    const resolvedStartIcon =
      loading && startIcon ? (
        <Box component="span" sx={sx.startIconSlot}>
          <Box component="span" sx={sx.hiddenStartIcon} aria-hidden>
            {startIcon}
          </Box>
          <Box component="span" sx={sx.startIconLoaderContainer}>
            {loader}
          </Box>
        </Box>
      ) : (
        startIcon
      );

    return (
      <MuiButton
        ref={ref}
        variant={variant}
        {...props}
        startIcon={resolvedStartIcon}
        aria-busy={loading || undefined}
        sx={{ pointerEvents: loading ? 'none' : undefined, ...(buttonSx as object) }}
      >
        <Box component="span" sx={sx.content}>
          {loading && !startIcon && !withArrow && loader}
          {children}
          {withArrow && (
            <Box component="span" aria-hidden sx={sx.arrowChip(variant, props.size)}>
              {loading ? (
                <CristaSpinner size={14} color="currentColor" />
              ) : (
                <ArrowForwardRoundedIcon />
              )}
            </Box>
          )}
        </Box>
      </MuiButton>
    );
  },
);

Button.displayName = 'Button';

export type { ButtonProps } from '@mui/material/Button';
export default Button;
